/**
 * TCL tabs — a generic WAI-ARIA tablist controller.
 *
 * Binds any `[data-tcl-tabs]` containing `[role="tab"]` buttons and
 * `[data-tcl-tab-panel]` panels. Server-rendered markup already carries the
 * correct `aria-selected` / `tabindex` / `hidden` state, so this script adds
 * behaviour and never initial appearance: with it absent, panel 1 is a complete
 * working panel and the tabs are inert buttons.
 *
 * Pairing is by `aria-controls` -> element id, not by index. Index pairing breaks
 * the moment a tab or panel is conditionally skipped — and the caller DOES skip
 * blank labels, so a stray comma in the merchant's setting would otherwise
 * silently shift every panel one place.
 *
 * ── THE PART THAT IS NOT BOILERPLATE ────────────────────────────────────────
 * A hidden panel has NO BOX, so anything inside it measured `0` wide at bind
 * time. `assets/tcl-before-after.js` reads `media.clientWidth` when it binds and
 * uses it for the Draggable bounds and for every percent<->pixel conversion, so a
 * slider inside a hidden tab panel binds with `width = 1`: the divider would move
 * one pixel across the whole image.
 *
 * That engine observes its own media with a ResizeObserver and re-applies bounds,
 * and `display: none` -> shown does fire it, so it self-heals. This script does
 * NOT rely on that being true: on every activation it also dispatches a `resize`
 * event, which is the engine's own no-ResizeObserver fallback path. Two
 * independent routes to the same `refresh()`, because the failure is silent — a
 * slider that looks perfect and simply will not drag.
 */

(function () {
  'use strict';

  var TAB = '[role="tab"]';

  function panelFor(tab) {
    var id = tab.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  function initGroup(group) {
    if (group.dataset.tclTabsBound === 'true') return;
    group.dataset.tclTabsBound = 'true';

    /* Scoped to THIS group's own tablist. `group.querySelectorAll` would also
       collect the tabs of a nested tab set, and two controllers would then fight
       over the same buttons. */
    var list = group.querySelector('[role="tablist"]');
    if (!list) return;
    var tabs = Array.prototype.slice.call(list.querySelectorAll(TAB));
    if (tabs.length < 2) return;

    function select(tab, moveFocus) {
      tabs.forEach(function (candidate) {
        var isTarget = candidate === tab;
        var panel = panelFor(candidate);
        candidate.setAttribute('aria-selected', isTarget ? 'true' : 'false');
        /* Roving tabindex: exactly one tab is in the tab order, so Tab moves out
           of the tablist rather than through every tab in it. */
        candidate.setAttribute('tabindex', isTarget ? '0' : '-1');
        if (panel) panel.hidden = !isTarget;
      });

      /* Fired AFTER the panel is shown, so anything measuring inside it sees a
         real box. See the header note — this is the belt to the engine's braces. */
      window.dispatchEvent(new Event('resize'));

      /* Focus only on a deliberate keyboard move. Focusing on click would steal
         the ring onto a control the pointer user did not ask to focus. */
      if (moveFocus) tab.focus();
    }

    tabs.forEach(function (tab, index) {
      tab.addEventListener('click', function () {
        select(tab, false);
      });

      tab.addEventListener('keydown', function (event) {
        var next = null;
        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            next = tabs[(index + 1) % tabs.length];
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            next = tabs[(index - 1 + tabs.length) % tabs.length];
            break;
          case 'Home':
            next = tabs[0];
            break;
          case 'End':
            next = tabs[tabs.length - 1];
            break;
          default:
            return;
        }
        /* Only after a key we handled — an unconditional preventDefault would
           also swallow Tab, Enter and Space on the button. */
        event.preventDefault();
        select(next, true);
      });
    });
  }

  function init(node) {
    (node || document).querySelectorAll('[data-tcl-tabs]').forEach(initGroup);
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', function () { init(); });

  /* Theme editor: a section re-render replaces the markup, so the bound flag is
     gone with it and initGroup runs again on the new nodes. */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  }
})();
